import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Канал наполнения Inspector контентом из экранов. Панель — часть shell (frozen
 * геометрия), но её содержимое приходит от выбранного объекта воркспейса.
 * Selection-контракт: контекст Inspector = видимо выбранный объект текущего экрана;
 * при смене маршрута контент предыдущего экрана сбрасывается.
 */
export interface InspectorApi {
  open: boolean;
  content: ReactNode | null;
  category: string | null;
  /** Показать контент и раскрыть панель. */
  show: (content: ReactNode, category?: string) => void;
  toggle: () => void;
  close: () => void;
  /** Полностью очистить (например при уходе с экрана). */
  clear: () => void;
}

const InspectorCtx = createContext<InspectorApi | null>(null);

export function useInspector(): InspectorApi {
  const ctx = useContext(InspectorCtx);
  if (!ctx) throw new Error('useInspector() вызван вне <InspectorProvider>');
  return ctx;
}

export function InspectorProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  /** Путь, для которого был вызван show() — обновляется синхронно, до эффектов провайдера. */
  const shownForPath = useRef<string | null>(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const show = useCallback((next: ReactNode, cat?: string) => {
    shownForPath.current = pathRef.current;
    setContent(next);
    setCategory(cat ?? null);
    // На телефоне bottom-sheet не открывается сам (закрывал бы 60% экрана при загрузке
    // объектных страниц) — его открывают кнопкой «Инспектор» в тулбаре или «Детали».
    const phone = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches;
    if (!phone) setOpen(true);
  }, []);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => setOpen(false), []);
  const clear = useCallback(() => {
    shownForPath.current = null;
    setContent(null);
    setCategory(null);
    setOpen(false);
  }, []);

  // Смена маршрута: контент чужого экрана не остаётся в панели (эффекты детей уже отработали).
  useEffect(() => {
    if (shownForPath.current !== null && shownForPath.current !== location.pathname) {
      shownForPath.current = null;
      setContent(null);
      setCategory(null);
      setOpen(false);
    }
  }, [location.pathname]);

  const value = useMemo<InspectorApi>(
    () => ({ open, content, category, show, toggle, close, clear }),
    [open, content, category, show, toggle, close, clear],
  );

  return <InspectorCtx.Provider value={value}>{children}</InspectorCtx.Provider>;
}
